import React, { useEffect, useState } from 'react';
import { Users, Package, DollarSign, TrendingUp } from 'lucide-react';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useNavigate } from 'react-router-dom';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalArtworks: 0,
    totalSales: 0,
    revenue: 0,
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const artworksSnapshot = await getDocs(collection(db, 'artworks'));
      
      setStats({
        totalUsers: usersSnapshot.size,
        totalArtworks: artworksSnapshot.size,
        totalSales: 0, // Implement when you have orders
        revenue: 0, // Implement when you have orders
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const statCards = [
    { icon: Users, label: 'Total Users', value: stats.totalUsers, color: 'bg-blue-500' },
    { icon: Package, label: 'Total Artworks', value: stats.totalArtworks, color: 'bg-green-500' },
    { icon: DollarSign, label: 'Total Sales', value: stats.totalSales, color: 'bg-purple-500' },
    { icon: TrendingUp, label: 'Revenue', value: `₹${stats.revenue}`, color: 'bg-orange-500' },
  ];

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Admin Dashboard</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="text-white" size={24} />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-1">{stat.value}</h3>
            <p className="text-gray-600">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-gray-50 transition-colors"
          >
            <Users className="mx-auto mb-2 text-primary" size={32} />
            <p className="text-sm font-medium text-gray-700">Manage Users</p>
          </button>
          <button
            onClick={() => navigate('/admin/artworks')}
            className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-gray-50 transition-colors"
          >
            <Package className="mx-auto mb-2 text-primary" size={32} />
            <p className="text-sm font-medium text-gray-700">Manage Artworks</p>
          </button>
          <button
            onClick={() => navigate('/admin/reports')}
            className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-gray-50 transition-colors"
          >
            <TrendingUp className="mx-auto mb-2 text-primary" size={32} />
            <p className="text-sm font-medium text-gray-700">View Reports</p>
          </button>
          <button
            onClick={() => navigate('/admin/settings')}
            className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-gray-50 transition-colors"
          >
            <DollarSign className="mx-auto mb-2 text-primary" size={32} />
            <p className="text-sm font-medium text-gray-700">Settings</p>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Recent Activity</h2>
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Users className="text-green-600" size={20} />
            </div>
            <div>
              <p className="font-medium text-gray-800">New user registered</p>
              <p className="text-sm text-gray-500">2 minutes ago</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Package className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="font-medium text-gray-800">New artwork posted</p>
              <p className="text-sm text-gray-500">15 minutes ago</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default AdminDashboard;